import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

/**
 * Renders a single MCQ question with options and Previous/Next (or Submit) navigation.
 *
 * When `disabled` is true (e.g., candidate proctor camera is off) option selection
 * and nav buttons are blocked visually + functionally.
 */
export default function QuestionPane({
  question,
  index,
  totalQ,
  answers,
  onSelect,
  onPrev,
  onNext,
  onSubmit,
  submitting,
  disabled = false,
}) {
  if (!question) return null;
  const answered = Object.keys(answers).length;
  const isLast = index === totalQ - 1;
  const selectedLabel = answers[String(index)];

  return (
    <Card className="border-border bg-card" data-testid="test-question-card">
      <CardContent className="p-6">
        {question.scenario && (
          <div className="bg-secondary/50 rounded-lg p-4 mb-4 text-sm text-muted-foreground italic leading-relaxed">
            {question.scenario}
          </div>
        )}
        <h3 className="text-base font-medium mb-5 leading-relaxed">
          Q{index + 1}. {question.question_text}
        </h3>

        <div className="space-y-2">
          {question.options.map((opt) => {
            let styleClass = "border-border bg-card hover:border-primary/30 text-muted-foreground hover:text-foreground";
            if (disabled) styleClass = "opacity-50 cursor-not-allowed border-border bg-secondary/30";
            else if (selectedLabel === opt.label) styleClass = "border-primary bg-primary/10 text-foreground";

            return (
              <button
                key={opt.label}
                onClick={() => onSelect(index, opt.label)}
                disabled={disabled}
                className={`w-full text-left rounded-lg border p-4 text-sm transition-colors ${styleClass}`}
                data-testid={`option-${opt.label}`}
              >
                <span className="font-semibold mr-3">{opt.label}.</span>
                {opt.text}
              </button>
            );
          })}
        </div>

        <div className="flex justify-between mt-6">
          <Button variant="outline" onClick={onPrev} disabled={index === 0 || disabled}>
            Previous
          </Button>
          {!isLast ? (
            <Button onClick={onNext} disabled={disabled}>
              Next
            </Button>
          ) : (
            <Button
              onClick={onSubmit}
              disabled={submitting || answered === 0 || disabled}
              className="btn-press"
              data-testid="question-pane-submit-button"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Submit Test
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
